import { api, workspacesApi, type Workspace } from "@/lib/api";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import { orderServiceId, orderWorkspaceId } from "@/lib/billinGoRoutes";
import { readLastWorkspace } from "@/lib/lastWorkspaceStorage";

type OrderLike = {
  serviceId?: string;
  workspaceId?: string;
  createdAt?: string;
  updatedAt?: string;
  workspace?: { id?: string };
};

function unwrapWorkspaceList(data: unknown): Workspace[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const r = data as Record<string, unknown>;
    if (Array.isArray(r.data)) return r.data as Workspace[];
    if (Array.isArray(r.workspaces)) return r.workspaces as Workspace[];
  }
  return [];
}

async function listUserWorkspaceIds(): Promise<Set<string>> {
  const data = await workspacesApi.list();
  const list = unwrapWorkspaceList(data);
  return new Set(list.map((w) => w.id).filter(Boolean));
}

async function isWorkspaceOwnedByUser(workspaceId: string): Promise<boolean> {
  const id = workspaceId.trim();
  if (!id) return false;
  const ids = await listUserWorkspaceIds();
  return ids.has(id);
}

/** Latest buyer order for a service (by updatedAt). */
export async function getWorkspaceIdFromLatestOrderForService(
  serviceId: string,
): Promise<string | null> {
  const response = await api.orders.list({ role: "buyer" });
  let orderList: OrderLike[] = [];
  if (Array.isArray(response)) {
    orderList = response as OrderLike[];
  } else if (response && typeof response === "object") {
    const r = response as Record<string, unknown>;
    if (Array.isArray(r.orders)) orderList = r.orders as OrderLike[];
    else if (Array.isArray(r.data)) orderList = r.data as OrderLike[];
  }

  const matching = orderList.filter(
    (o) => orderServiceId(o) === serviceId.trim(),
  );
  if (matching.length === 0) return null;

  matching.sort((a, b) => {
    const ta = Date.parse(a.updatedAt || a.createdAt || "") || 0;
    const tb = Date.parse(b.updatedAt || b.createdAt || "") || 0;
    return tb - ta;
  });

  const wid = orderWorkspaceId(matching[0]);
  return wid || null;
}

async function getWorkspaceIdForServiceViaWorkspaces(
  serviceId: string,
): Promise<string | null> {
  const list = (await workspacesApi.list()) as unknown as {
    workspaces?: Array<{ id: string; createdAt?: string; updatedAt?: string }>;
  };
  const workspaces = Array.isArray(list?.workspaces)
    ? list.workspaces
    : unwrapWorkspaceList(list);

  if (workspaces.length === 0) return null;

  const sorted = [...workspaces].sort((a, b) => {
    const ta = Date.parse(a.updatedAt || a.createdAt || "") || 0;
    const tb = Date.parse(b.updatedAt || b.createdAt || "") || 0;
    return tb - ta;
  });

  for (const w of sorted.slice(0, 8)) {
    try {
      const order = await api.orders.getByWorkspaceAndService(w.id, serviceId);
      if (order) return w.id;
    } catch {
      // ignore
    }
  }
  return null;
}

/**
 * Resolve workspace for a hired service: localStorage last pick → API last created
 * → latest order → workspace scan.
 */
export async function resolveWorkspaceForService(
  serviceId: string,
  opts?: { preferredWorkspaceId?: string | null },
): Promise<string | null> {
  const sid = serviceId.trim();
  if (!sid) return null;

  const candidates: string[] = [];
  const preferred =
    opts?.preferredWorkspaceId?.trim() || readLastWorkspace();
  if (preferred) candidates.push(preferred);

  const fromLast =
    await workspacesApi.getLastCreatedWorkspaceIdByService(sid);
  if (typeof fromLast === "string" && fromLast.trim()) {
    candidates.push(fromLast.trim());
  }

  const fromOrder = await getWorkspaceIdFromLatestOrderForService(sid);
  if (fromOrder) candidates.push(fromOrder);

  const seen = new Set<string>();
  for (const id of candidates) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (await isWorkspaceOwnedByUser(id)) return id;
  }

  const viaScan = await getWorkspaceIdForServiceViaWorkspaces(sid);
  if (viaScan && (await isWorkspaceOwnedByUser(viaScan))) return viaScan;

  return null;
}

/** /go/billlin — strict service resolve แล้ว fallback แบบ signin (LIFF / order) */
export async function resolveBillinWorkspaceForGo(
  opts?: { preferredWorkspaceId?: string | null },
): Promise<string | null> {
  const preferred = opts?.preferredWorkspaceId?.trim() || readLastWorkspace();
  const fromService = await resolveWorkspaceForService(BILLIN_SERVICE_ID, {
    preferredWorkspaceId: preferred,
  });
  if (fromService) return fromService;
  return resolveBillinWorkspaceIdForSignin(BILLIN_SERVICE_ID);
}

/**
 * /signin หลังเชื่อมต่อ (Billin) — ลำดับตาม product:
 * 1. มี `aera_last_workspace` ใน localStorage → ใช้ workspace นั้น (ถ้าเป็นของ user)
 * 2. ไม่มี → workspace จาก order ล่าสุดของ service นี้
 * 3. ไม่มีทั้งคู่ (ไม่มี workspace / ไม่เคย order service นี้) → null → onboarding
 */
export async function resolveBillinWorkspaceIdForSignin(
  serviceId: string = BILLIN_SERVICE_ID,
): Promise<string | null> {
  const sid = serviceId.trim();
  if (!sid) return null;

  const fromStorage = readLastWorkspace();
  if (fromStorage?.trim()) {
    const id = fromStorage.trim();
    try {
      if (await isWorkspaceOwnedByUser(id)) return id;
    } catch {
      // LIFF / WebView: trust last pick when ownership API is temporarily unavailable
      return id;
    }
    // มีใน storage แต่ไม่ใช่ของ user — ลอง order ล่าสุดต่อ
  }

  const fromLatestOrder = await getWorkspaceIdFromLatestOrderForService(sid);
  if (fromLatestOrder) {
    // Buyer order มาจาก JWT ของ user นี้แล้ว — ใช้ได้แม้ workspaces/list ไม่ครบ
    try {
      if (await isWorkspaceOwnedByUser(fromLatestOrder)) return fromLatestOrder;
    } catch {
      return fromLatestOrder;
    }
    return fromLatestOrder;
  }

  const fromLastCreated =
    await workspacesApi.getLastCreatedWorkspaceIdByService(sid);
  if (fromLastCreated?.trim()) {
    const id = fromLastCreated.trim();
    try {
      if (await isWorkspaceOwnedByUser(id)) return id;
    } catch {
      return id;
    }
  }

  return null;
}
